import React from "react";
import { Field } from "redux-form";
import { TextField } from "redux-form-material-ui";
import { isRequired } from "js/helpers/validations";
import { _t } from "js/utils/TranslationService";
import { numberWithCommas } from "js/utils";
import { styles } from "../styles.js";

/**
 * Componente InputNumberWithNegativeField
 * Diseñado para manejar números positivos, negativos, enteros y decimales.
 * Utiliza lógica interna aislada para evitar bucles de renderizado y problemas de estabilidad.
 */

// Validación interna de número
const isNumeric = value => {
  if (!value && value !== 0) return undefined;
  if (value === "-") return undefined;
  const val = typeof value === "string" ? value.replace(",", ".") : value;
  return isNaN(Number(val)) ? "Debe ser un número" : undefined;
};

// Normalización interna para manejar ceros con signo, comas y puntos
const normalizeNegative = (decimalNumbers = 2, allowNegative = true) => (
  value = ""
) => {
  if (value === undefined || value === null || value === "") return "";
  const valStr = value.toString().trim();
  if (valStr === "") return "";

  const isNegative = allowNegative && valStr.startsWith("-");
  let clean = valStr;

  if (decimalNumbers === 0) {
    clean = clean
      .replace(/[.,]/g, "")
      .replace(allowNegative ? /[^-0-9]/g : /[^0-9]/g, "");
  } else {
    const hasComma = clean.includes(",");
    if (hasComma) {
      clean = clean.replace(/\./g, "").replace(",", ".");
    }
    clean = clean.replace(allowNegative ? /[^-0-9.]/g : /[^0-9.]/g, "");
  }

  if (allowNegative && clean.includes("-")) {
    clean = (isNegative ? "-" : "") + clean.replace(/-/g, "");
  }

  // Verificar si hay dígitos después de limpiar
  const digits = clean.replace(/[^0-9]/g, "");
  if (digits === "") {
    if (clean === "." || clean === "-.") return isNegative ? "-0," : "0,";
    if (allowNegative && clean === "-") return "-";
    return "";
  }

  if (clean.endsWith(".")) return clean.replace(".", ",");

  return clean;
};

// Formateo interno con separadores de miles y decimales
const formatNegative = (decimalNumbers = 2, allowNegative = true) => (
  value = ""
) => {
  if (value === undefined || value === null || value === "") return undefined;
  const valStr = value.toString().trim();

  // Casos especiales durante la escritura
  if (
    allowNegative &&
    (valStr === "-" || valStr === "-," || valStr === "-0" || valStr === "-0,")
  )
    return valStr;

  const startsWithMinus = allowNegative && valStr.startsWith("-");
  const endsWithComma = valStr.endsWith(",");

  let clean = valStr.replace(",", ".");
  if (decimalNumbers === 0) {
    clean = clean.replace(/[.]/g, "");
  }

  const num = parseFloat(clean);
  if (isNaN(num)) return startsWithMinus ? "-" : "";

  let formatted = numberWithCommas(num, decimalNumbers);

  // Forzar el signo si numberWithCommas lo pierde (ej: -0)
  if (startsWithMinus && !formatted.startsWith("-")) {
    formatted = "-" + formatted;
  }

  return endsWithComma ? formatted + "," : formatted;
};

const isNavigationKey = e => {
  const nav = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End"
  ];
  return nav.includes(e.key) || e.ctrlKey || e.metaKey;
};

// Manejo de eventos de teclado restringido
const handleKeyDown = (decimalNumbers, allowDecimal, allowNegative) => e => {
  if (isNavigationKey(e)) return;

  const { value = "", selectionStart = 0 } = e.target;
  const key = e.key;

  // Permitir signo negativo al principio
  if (
    allowNegative &&
    key === "-" &&
    selectionStart === 0 &&
    !value.includes("-")
  )
    return;

  // Permitir números
  if (key >= "0" && key <= "9") return;

  // Permitir separadores decimales si aplica
  if (allowDecimal && (key === "," || key === ".")) {
    if (!value.includes(",") && !value.includes(".")) return;
  }

  e.preventDefault();
};

// Cache de funciones para evitar recreación innecesaria en cada render (estabilidad Redux-Form)
const normalizeCache = new Map();
const formatCache = new Map();

const getNormalize = (decimals, allowNegative) => {
  const key = `${decimals}-${allowNegative}`;
  if (!normalizeCache.has(key))
    normalizeCache.set(key, normalizeNegative(decimals, allowNegative));
  return normalizeCache.get(key);
};

const getFormat = (decimals, allowNegative) => {
  const key = `${decimals}-${allowNegative}`;
  if (!formatCache.has(key))
    formatCache.set(key, formatNegative(decimals, allowNegative));
  return formatCache.get(key);
};

const InputNumberWithNegativeField = ({
  name,
  label,
  placeholder,
  required,
  classes,
  disabled,
  translation,
  customValidations = [],
  InputProps,
  helperText,
  isFloat = false,
  decimalNumbers = 2,
  allowNegative = true,
  variant
}) => {
  const decimals = isFloat ? decimalNumbers : 0;

  return (
    <Field
      type="text"
      required={required}
      name={name}
      label={translation || _t(label)}
      placeholder={placeholder}
      component={TextField}
      disabled={disabled}
      fullWidth
      format={getFormat(decimals, allowNegative)}
      normalize={getNormalize(decimals, allowNegative)}
      validate={(() => {
        let validations = [];
        if (required) validations.push(isRequired);
        validations.push(isNumeric);
        return validations.concat(customValidations);
      })()}
      className={classes ? classes.field : styles.field}
      InputProps={InputProps}
      inputProps={{
        pattern: isFloat
          ? allowNegative
            ? "[-0-9.,]*"
            : "[0-9.,]*"
          : allowNegative
            ? "[-0-9.]*"
            : "[0-9.]*",
        inputMode: isFloat ? "decimal" : "numeric"
      }}
      onKeyDown={handleKeyDown(decimals, isFloat, allowNegative)}
      InputLabelProps={{ shrink: true }}
      helperText={helperText}
      variant={variant}
    />
  );
};

export default InputNumberWithNegativeField;
